import { SportType } from 'modules/core/domain/enums/sport-type';
import { normalizeMatchLayout } from 'modules/manager/domain/match-layout';
import {
  MediaKind,
  canvasTemplateTypeFor,
  isKnownMediaKind,
  primaryKindForTemplateType,
} from 'modules/manager/domain/media-kind';
import { normalizePlayerRanking } from 'modules/manager/domain/player-stats';
import { isKnownTemplateType } from 'modules/manager/domain/template-type';
import { DEFAULT_TEMPLATE_OBJECTS_VERSION, TEMPLATE_FILE_FORMAT, TEMPLATE_FILE_VERSION, TemplateFileEntry } from './template-file';

/** Why the whole file was rejected — nothing is imported from it. */
export type TemplateFileError = 'not-json' | 'wrong-format' | 'newer-version' | 'empty';

/** Why one entry of an otherwise valid file was skipped. */
export type TemplateEntryProblem = 'not-object' | 'no-name' | 'no-objects' | 'bad-size' | 'unknown-template-type';

export interface SkippedTemplateEntry {
  /** Position in the file's `templates`, zero-based. */
  index: number;
  name?: string;
  reason: TemplateEntryProblem;
}

export type ParsedTemplateFile = { error: TemplateFileError } | { entries: TemplateFileEntry[]; skipped: SkippedTemplateEntry[] };

const SPORT_TYPES: ReadonlySet<string> = new Set(Object.values(SportType));

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isPositiveNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function resolveMediaKind(rawKind: unknown, templateType: string | undefined): MediaKind | null {
  if (isKnownMediaKind(rawKind as string | undefined | null)) {
    return rawKind as MediaKind;
  }
  if (typeof templateType === 'string') {
    return primaryKindForTemplateType(templateType);
  }
  return null;
}

function parseEntry(raw: unknown): TemplateFileEntry | TemplateEntryProblem {
  if (!isRecord(raw)) return 'not-object';
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';
  if (!name) return 'no-name';
  if (!Array.isArray(raw.objects)) return 'no-objects';
  if (!isPositiveNumber(raw.width) || !isPositiveNumber(raw.height)) return 'bad-size';
  // Unlike the data mapper, which has no choice (the row is already in the DB),
  // import may refuse: silently turning a table into a schedule is worse than
  // saying this stand does not know the type. A missing `mediaKind` is recovered
  // from the primary mapping of a known `templateType`.
  const mediaKind = resolveMediaKind(raw.mediaKind, raw.templateType as string | undefined);
  if (mediaKind === null) return 'unknown-template-type';
  const templateType = isKnownTemplateType(raw.templateType as string | undefined)
    ? (raw.templateType as TemplateFileEntry['templateType'])
    : canvasTemplateTypeFor(mediaKind);

  return {
    name,
    version: typeof raw.version === 'string' && raw.version ? raw.version : DEFAULT_TEMPLATE_OBJECTS_VERSION,
    width: raw.width,
    height: raw.height,
    sportType: typeof raw.sportType === 'string' && SPORT_TYPES.has(raw.sportType) ? (raw.sportType as SportType) : null,
    templateType,
    mediaKind,
    templateSubtypes: Array.isArray(raw.templateSubtypes)
      ? raw.templateSubtypes.filter((s): s is string => typeof s === 'string')
      : [],
    matchLayout: normalizeMatchLayout(raw.matchLayout),
    playerRanking: normalizePlayerRanking(raw.playerRanking),
    objects: raw.objects as TemplateFileEntry['objects'],
  };
}

/**
 * Reads a template file. The file is rejected as a whole only when it is not
 * ours, is from a newer format version or holds no templates; a broken entry is
 * skipped with a reason and its neighbours still import.
 */
export function parseTemplateFile(text: string): ParsedTemplateFile {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { error: 'not-json' };
  }

  if (!isRecord(data) || data.format !== TEMPLATE_FILE_FORMAT || !Array.isArray(data.templates)) {
    return { error: 'wrong-format' };
  }
  if (typeof data.formatVersion !== 'number' || data.formatVersion > TEMPLATE_FILE_VERSION) {
    return { error: 'newer-version' };
  }
  if (data.templates.length === 0) return { error: 'empty' };

  const entries: TemplateFileEntry[] = [];
  const skipped: SkippedTemplateEntry[] = [];
  data.templates.forEach((raw, index) => {
    const result = parseEntry(raw);
    if (typeof result === 'string') {
      const name = isRecord(raw) && typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim() : undefined;
      skipped.push(name === undefined ? { index, reason: result } : { index, name, reason: result });
    } else {
      entries.push(result);
    }
  });
  return { entries, skipped };
}
